import { formatMoney } from "theme";
import {
  trackClick,
  helloRetailRecommendationKey,
  helloRetailStorefrontProductUrl,
  shopifyAlternateHreflang,
  alternateHrefFromProductHtml,
  productJsonUrlFromProductPageHref,
} from "customHRUtils";

class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
  }

  async connectedCallback() {
    if (
      !window.themeVariables.settings.cartRecommendationKeyMobile ||
      !window.themeVariables.settings.cartRecommendationKeyDesktop ||
      !window.themeVariables.settings.helloRetailId
    ) {
      return;
    }

    this.abortController = new AbortController();
    this.mediaType = this.dataset.media || "mobile";

    this.key = helloRetailRecommendationKey(
      this.mediaType == "mobile"
        ? window.themeVariables.settings.cartRecommendationKeyMobile
        : window.themeVariables.settings.cartRecommendationKeyDesktop,
    );

    document.addEventListener(
      "recoms-loaded",
      (event) => {
        const response = event.detail.responses.find((response) => response.key == this.key);
        if (response) {
          this.init(response);
        }
      },
      { signal: this.abortController.signal },
    );

    document.addEventListener(
      "cart:recoms-loaded",
      (event) => {
        const response = event.detail.responses.find((response) => response.key == this.key);
        if (response) {
          this.init(response);
        }
      },
      { signal: this.abortController.signal },
    );
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  async init(response) {
    const products = await this.loadRecommendations(response);

    this.product_list = this.querySelector("[data-product-list]") || this.querySelector(".splide__list");

    if (this.mediaType === "mobile") {
      this.product_list.innerHTML = products.map(this.createProductHtml.bind(this, "mobile")).join("");

      this.placement = this.querySelector("[data-placement]").dataset.placement || "cart-drawer";

      if (this.placement === "cart-drawer") {
        this.splide = new Splide(".splide", {
          gap: 10,
          pagination: false,
          mediaQuery: "min",
          arrows: false,
          drag: "free",
        });
      } else {
        this.splide = new Splide(".splide", {
          gap: 10,
          pagination: false,
          perPage: 3,
          mediaQuery: "min",
          arrows: false,
          drag: "free",
          breakpoints: {
            750: {
              perPage: 4,
            },
            990: {
              perPage: 5,
            },
            1200: {
              perPage: 6,
            },
          },
        });
      }

      this.splide.mount();
    } else {
      this.product_list.innerHTML = products.map(this.createProductHtml.bind(this, "desktop")).join("");
    }

    const productElements = this.product_list.querySelectorAll("[data-tracking-code]");
    productElements.forEach((productElement) =>
      productElement.querySelectorAll("a, button").forEach((productLink) => {
        productLink.addEventListener("click", (event) => {
          event.preventDefault();
          trackClick(productElement.dataset.trackingCode);
        });
      }),
    );
  }

  createProductHtml(mediaType, product) {
    const isOnSale =
      product.variants[0].compare_at_price > product.variants[0].price
        ? `
        <span class="cart-upsell-item__price--sale">${formatMoney(product.variants[0].price)}</span>
        <del class="cart-upsell-item__price--compare">${formatMoney(product.variants[0].compare_at_price)}</del>
      `
        : `
      <span class="cart-upsell-item__price--normal">${formatMoney(product.variants[0].price)} </span>
    `;

    const productUrl = `/products/${product.handle}`;

    const quickBuyId = `quick-buy-cart-drawer-${this.mediaType}-${product.id}`;

    const productHtml = `
      <div class="cart-upsell-item" data-tracking-code="${product.trackingCode}">
        <a href="${productUrl}" class="cart-upsell-item__image">
          <img src="${product.image.src}&width=200" alt="${product.title}"/>
        </a>
        <a href="${productUrl}" class="cart-upsell-item__title">${product.title}</a>
        <div class="cart-upsell-item__prices">
          ${isOnSale}
        </div>
        <button type="button" aria-controls="${quickBuyId}" class="button">
          <span class="sr-only">{{ 'product.general.choose_options' | t }}</span>
          ${window.themeVariables.strings.addToCartButtonShort}
        </button>
  
        <quick-buy-modal handle="${product.handle}" class="quick-buy-modal modal" id="${quickBuyId}">
        </quick-buy-modal>
      </div>
    `;

    if (mediaType === "mobile") {
      return `<li class="splide__slide">${productHtml}</li>`;
    } else {
      return productHtml;
    }
  }

  async loadRecommendations(response) {
    const responseProducts = response.products;

    if (!responseProducts || responseProducts.length === 0) {
      return [];
    }

    const hreflang = shopifyAlternateHreflang();

    const fetchProductJson = async (rp) => {
      const primaryUrl = `${helloRetailStorefrontProductUrl(rp.originalUrl)}.json`;
      let res = await fetch(primaryUrl);
      if (res.ok) {
        const data = await res.json();
        return data?.product ? { rp, product: data.product } : null;
      }
      if (res.status !== 404 || !hreflang) {
        return null;
      }
      const dkRes = await fetch(rp.originalUrl);
      if (!dkRes.ok) {
        return null;
      }
      const html = await dkRes.text();
      const altHref = alternateHrefFromProductHtml(html, hreflang);
      if (!altHref) {
        return null;
      }
      const jsonUrl = productJsonUrlFromProductPageHref(altHref, rp.originalUrl);
      if (!jsonUrl) {
        return null;
      }
      res = await fetch(jsonUrl);
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      return data?.product ? { rp, product: data.product } : null;
    };

    const pairs = await Promise.all(responseProducts.map(fetchProductJson));
    const mappedProducts = [];
    for (const pair of pairs) {
      if (!pair) continue;
      pair.product.trackingCode = pair.rp.trackingCode;
      mappedProducts.push(pair.product);
    }

    return mappedProducts;
  }
}

if (!customElements.get("cart-upsell")) {
  customElements.define("cart-upsell", CartUpsell);
}
